import { useEffect, useRef, useState } from "react";
import { buildReceiptHTML } from "@/components/ReceiptTemplate";
import { printReceipt } from "@/utils/printReceipt";
import { X, Printer, FileText } from "lucide-react";

/**
 * Preview a thermal receipt inside a modal iframe (defaults to 58mm width for
 * restaurant NyxPrinter). Provides a "Print" button that triggers the actual
 * printer with the same paper size.
 */
export default function ReceiptPreviewModal({ order, onClose, paper = "58mm" }) {
  const iframeRef = useRef(null);
  const [html, setHtml] = useState("");

  useEffect(() => {
    if (!order) return;
    try {
      setHtml(buildReceiptHTML(order, { paper }));
    } catch (e) {
      setHtml(`<div style="padding:20px;font-family:sans-serif;color:#b91c1c">فشل توليد الإيصال: ${String(e?.message || e)}</div>`);
    }
  }, [order, paper]);

  if (!order) return null;

  const doPrint = async () => {
    try { await printReceipt(order, { paper }); } catch (e) { window.alert("تعذّرت الطباعة: " + (e?.message || e)); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
      data-testid="receipt-preview-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: "94vh" }}
      >
        <div className="flex justify-between items-center p-4 border-b border-[var(--border)] bg-gradient-to-l from-primary-brand to-[#1E3AB0] text-white">
          <div className="flex items-center gap-2">
            <FileText size={18}/>
            <h2 className="text-lg font-black" style={{fontFamily:'Cairo'}}>
              عرض الإيصال {order.daily_seq ? `#${order.daily_seq}` : ""}
            </h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center" data-testid="receipt-preview-close">
            <X size={16}/>
          </button>
        </div>

        <div className="flex-1 overflow-auto bg-[var(--surface-alt)] p-3 flex items-start justify-center">
          <iframe
            ref={iframeRef}
            srcDoc={html}
            title="Receipt Preview"
            data-testid="receipt-preview-iframe"
            style={{
              width: "240px",
              minHeight: "60vh",
              border: "1px solid var(--border)",
              background: "#fff",
              boxShadow: "0 6px 24px rgba(0,0,0,0.08)",
            }}
          />
        </div>

        <div className="p-4 border-t border-[var(--border)] flex gap-2">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl bg-[var(--surface-alt)] text-[var(--text-secondary)] font-bold" data-testid="receipt-preview-cancel">
            إغلاق
          </button>
          <button
            onClick={doPrint}
            className="flex-[2] py-3 rounded-xl bg-primary-brand text-white font-black flex items-center justify-center gap-2"
            data-testid="receipt-preview-print"
          >
            <Printer size={16}/> طباعة
          </button>
        </div>
      </div>
    </div>
  );
}
